import { useState } from 'react';
import { dueDateState, uuid } from '../utils';

export default function GoalsTab({ goals, saveGoal, deleteGoal }) {
  const [adding, setAdding] = useState(false);

  const active = goals.filter((g) => !g.done);
  const done = goals.filter((g) => g.done);

  return (
    <div className="goals-tab">
      <div className="tab-head">
        <h3>Goals</h3>
        <button className="link-btn" onClick={() => setAdding((v) => !v)} aria-expanded={adding}>
          {adding ? 'Cancel' : '+ New goal'}
        </button>
      </div>
      {adding && <GoalForm onSave={async (g) => { await saveGoal(g); setAdding(false); }} />}
      {active.length === 0 && !adding && (
        <p className="muted">Nothing on the list. Add one when you're ready.</p>
      )}
      <ul className="goals-list">
        {active.map((g) => (
          <GoalCard key={g.id} goal={g} saveGoal={saveGoal} deleteGoal={deleteGoal} />
        ))}
      </ul>
      {done.length > 0 && (
        <details className="goals-tab__done">
          <summary>{done.length} completed</summary>
          <ul className="goals-list">
            {done.map((g) => (
              <GoalCard key={g.id} goal={g} saveGoal={saveGoal} deleteGoal={deleteGoal} />
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function GoalForm({ onSave }) {
  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [milestones, setMilestones] = useState([]);
  const [milestoneText, setMilestoneText] = useState('');

  function addMilestone() {
    const t = milestoneText.trim();
    if (!t) return;
    setMilestones((prev) => [...prev, { id: uuid(), text: t, done: false }]);
    setMilestoneText('');
  }

  async function submit(e) {
    e.preventDefault();
    if (!title.trim()) return;
    await onSave({ title, dueDate: dueDate || null, milestones });
    setTitle('');
    setDueDate('');
    setMilestones([]);
  }

  return (
    <form className="goal-form" onSubmit={submit}>
      <input
        autoFocus
        placeholder="Goal"
        aria-label="Goal title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        required
      />
      <label className="goal-form__date">
        Due (optional)
        <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
      </label>
      <div className="goal-form__milestones">
        {milestones.map((m, i) => (
          <span key={m.id} className="chip">
            {m.text}
            <button
              type="button"
              onClick={() => setMilestones((prev) => prev.filter((_, idx) => idx !== i))}
              aria-label={`Remove milestone "${m.text}"`}
            >
              ×
            </button>
          </span>
        ))}
        <div className="goal-form__milestone-row">
          <input
            placeholder="Add a milestone"
            aria-label="Add a milestone"
            value={milestoneText}
            onChange={(e) => setMilestoneText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addMilestone();
              }
            }}
          />
          <button type="button" className="link-btn" onClick={addMilestone}>
            Add
          </button>
        </div>
      </div>
      <button type="submit" className="primary-btn">
        Save goal
      </button>
    </form>
  );
}

function GoalCard({ goal, saveGoal, deleteGoal }) {
  const total = goal.milestones?.length ?? 0;
  const completed = goal.milestones?.filter((m) => m.done).length ?? 0;
  const pct = total ? Math.round((completed / total) * 100) : 0;
  const due = dueDateState(goal.dueDate);

  function toggleMilestone(id) {
    const milestones = goal.milestones.map((m) => (m.id === id ? { ...m, done: !m.done } : m));
    saveGoal({ ...goal, milestones });
  }

  function toggleDone() {
    saveGoal({ ...goal, done: !goal.done });
  }

  return (
    <li className={`goal-card${goal.done ? ' goal-card--done' : ''}`}>
      <div className="goal-card__top">
        <button
          className="goal-card__check"
          onClick={toggleDone}
          aria-label={`Mark goal "${goal.title}" ${goal.done ? 'not done' : 'done'}`}
          aria-pressed={goal.done}
        >
          {goal.done ? '●' : '○'}
        </button>
        <h4 className="goal-card__title">{goal.title}</h4>
        {due && <span className={`badge badge--${due.state}`}>{due.label}</span>}
        <button
          className="link-btn goal-card__delete"
          onClick={() => deleteGoal(goal.id)}
          aria-label={`Delete goal "${goal.title}"`}
        >
          ×
        </button>
      </div>
      {total > 0 && (
        <div className="goal-card__progress">
          <div className="goal-card__bar" aria-hidden="true">
            <div className="goal-card__bar-fill" style={{ width: `${pct}%` }} />
          </div>
          <span className="goal-card__progress-label">
            {completed}/{total} milestones done
          </span>
        </div>
      )}
      {goal.milestones?.length > 0 && (
        <ul className="goal-card__milestones">
          {goal.milestones.map((m) => (
            <li key={m.id} className={m.done ? 'done' : ''}>
              <button
                className="goal-card__milestone-check"
                onClick={() => toggleMilestone(m.id)}
                aria-label={`Mark milestone "${m.text}" ${m.done ? 'not done' : 'done'}`}
                aria-pressed={m.done}
              >
                {m.done ? '✓' : '○'}
              </button>
              <span>{m.text}</span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
